import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
// The ring is drawn in a 100 × 100 box. The stroke width comes from the size class.
const RING_RADIUS = 42;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
/**
 * @export
 * A determinate or indeterminate progress bar, as a straight bar or a ring.
 *
 * @example
 * ```tsx
 * <ProgressBar value={uploadPercent} showLabel />
 * <ProgressBar indeterminate variant="info" size="small" />
 * <ProgressBar value={4} labelPosition="top" title="Setup" showLabel label="4 / 7" track="strong" />
 * <ProgressBar value={57} shape="circular" size="large" centerContent={<span>4 / 7</span>} />
 * ```
 *
 * @category Loaders
 */
const ProgressBar = (props) => {
    const { value, indeterminate, size = 'medium', variant = 'primary', showLabel, label, shape = 'linear', centerContent, labelPosition = 'end', title, track = 'subtle', className, style } = props;
    const percent = Math.min(100, Math.max(0, value || 0));
    const labelText = label !== null && label !== void 0 ? label : `${Math.round(percent)}%`;
    const circular = shape === 'circular';
    const top = !circular && labelPosition === 'top';
    const classes = classNames('uxpcore_progressbar', className, {
        [`uxpcore_progressbar--${size}`]: size !== 'medium',
        [`uxpcore_progressbar--${variant}`]: variant !== 'primary',
        'uxpcore_progressbar--indeterminate': indeterminate,
        'uxpcore_progressbar--circular': circular,
        'uxpcore_progressbar--label-top': top,
        'uxpcore_progressbar--track-strong': track === 'strong',
    });
    const aria = {
        role: 'progressbar',
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-valuenow': indeterminate ? undefined : Math.round(percent),
    };
    if (circular) {
        const center = centerContent !== null && centerContent !== void 0 ? centerContent : (showLabel ? labelText : null);
        return (_jsxs("div", Object.assign({ className: classes, style: style }, aria, { children: [_jsxs("svg", { className: "uxpcore_progressbar__ring", viewBox: "0 0 100 100", "aria-hidden": "true", children: [_jsx("circle", { className: "uxpcore_progressbar__ringtrack", cx: "50", cy: "50", r: RING_RADIUS }), _jsx("circle", { className: "uxpcore_progressbar__ringfill", cx: "50", cy: "50", r: RING_RADIUS, strokeDasharray: `${(RING_LENGTH * (indeterminate ? 25 : percent)) / 100} ${RING_LENGTH}`, transform: "rotate(-90 50 50)" })] }), center != null && _jsx("div", { className: "uxpcore_progressbar__center", children: center })] })));
    }
    const bar = (_jsx("div", { className: "uxpcore_progressbar__track", children: _jsx("div", { className: "uxpcore_progressbar__fill", style: indeterminate ? undefined : { width: `${percent}%` } }) }));
    if (top) {
        return (_jsxs("div", Object.assign({ className: classes, style: style }, aria, { children: [(title != null || showLabel) && (_jsxs("div", { className: "uxpcore_progressbar__header", children: [_jsx("div", { className: "uxpcore_progressbar__title", children: title }), showLabel && _jsx("div", { className: "uxpcore_progressbar__label", children: labelText })] })), bar] })));
    }
    return (_jsxs("div", Object.assign({ className: classes, style: style }, aria, { children: [bar, showLabel && (_jsx("div", { className: "uxpcore_progressbar__label", children: labelText }))] })));
};
export default ProgressBar;
